import type { MouseEventHandler } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { JorvioMark } from "@/components/brand/jorvio-mark";

export type AppLogoTone = "marketing" | "marketingDark" | "app";

type AppLogoProps = {
  href?: string;
  /** `marketing` = dark wordmark on light pages; `marketingDark` = light wordmark on dark hero; `app` = sidebar. */
  tone?: AppLogoTone;
  className?: string;
  size?: "sm" | "md";
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};

const sizeClasses = {
  sm: { wrap: "h-7 w-7", word: "text-[14px] sm:text-[15px]" },
  md: { wrap: "h-8 w-8", word: "text-[15px] sm:text-base" },
};

export function AppLogo({ href = "/", tone = "app", className, size = "md", onClick }: AppLogoProps) {
  const s = sizeClasses[size];
  /** `app`: inherit parent text so the mark works on dark sidebar (`text-slate-100`) and light mobile header. */
  const wordMuted =
    tone === "marketing" ? "text-slate-900" : tone === "marketingDark" ? "text-slate-100" : "text-inherit";
  const quotesAccent = tone === "marketingDark" ? "text-sky-300" : "text-primary";
  const dotMuted =
    tone === "marketingDark"
      ? "text-slate-500"
      : tone === "marketing"
        ? "text-muted-foreground/40"
        : "text-inherit opacity-55";

  const inner = (
    <>
      <span
        className={cn(
          "grid shrink-0 place-items-center overflow-hidden rounded-[11px] p-[2px] text-primary-foreground shadow-sm ring-1",
          "bg-primary ring-primary/25",
          s.wrap
        )}
      >
        <JorvioMark className="h-[88%] w-[88%] min-h-0 min-w-0" />
      </span>
      <span className={cn("font-semibold tracking-tight", s.word, wordMuted)}>
        <span className="tracking-[-0.02em]">Jorvio</span>
        <span className={cn("mx-1 select-none", dotMuted)} aria-hidden>
          ·
        </span>
        <span className={quotesAccent}>Quotes</span>
      </span>
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        onClick={onClick}
        className={cn("flex items-center gap-2.5", className)}
        aria-label="Jorvio Quotes home"
      >
        {inner}
      </Link>
    );
  }

  return (
    <span className={cn("flex items-center gap-2.5", className)} aria-label="Jorvio Quotes">
      {inner}
    </span>
  );
}
